import Konva from 'konva';
import WebFont from 'webfontloader';

class PolotnoSDK {
  constructor(containerId, options = {}) {
    this.containerId = containerId;
    this.container = document.getElementById(containerId);
    this.width = options.width || 1920;
    this.height = options.height || 1080;
    this.stage = null;
    this.layer = null;
    this.polotnoData = null;
    this.currentPageIndex = 0;
    this.loadedFonts = new Set();
    this.apiUrl = options.apiUrl || process.env.REACT_APP_BACKEND_URL + '/api';
    
    if (this.container) {
      this.initStage();
    }
  }

  initStage() {
    const containerWidth = this.container.offsetWidth || this.width;
    const scale = containerWidth / this.width;
    
    this.stage = new Konva.Stage({
      container: this.containerId,
      width: containerWidth,
      height: this.height * scale,
      scaleX: scale,
      scaleY: scale,
    });

    this.layer = new Konva.Layer();
    this.stage.add(this.layer);
  }

  async loadJSON(jsonData) {
    if (typeof jsonData === 'string') {
      this.polotnoData = JSON.parse(jsonData);
    } else {
      this.polotnoData = jsonData;
    }

    this.width = this.polotnoData.width || 1920;
    this.height = this.polotnoData.height || 1080;

    await this.loadFonts(this.polotnoData.fonts || []);
    await this.render();

    return this.polotnoData;
  }

  async loadFonts(fonts) {
    const uniqueFonts = [...new Set(fonts)];
    const fontsToLoad = uniqueFonts.filter(font => !this.loadedFonts.has(font));

    if (fontsToLoad.length === 0) return;

    return new Promise((resolve, reject) => {
      WebFont.load({
        google: {
          families: fontsToLoad
        },
        active: () => {
          fontsToLoad.forEach(font => this.loadedFonts.add(font));
          resolve();
        },
        inactive: () => {
          console.warn('Some fonts failed to load');
          resolve();
        },
        timeout: 5000
      });
    });
  }

  async render(pageIndex = 0) {
    if (!this.polotnoData || !this.polotnoData.pages) {
      throw new Error('No Polotno data loaded');
    }

    this.currentPageIndex = pageIndex;
    const page = this.polotnoData.pages[pageIndex];
    
    if (!page) {
      throw new Error(`Page ${pageIndex} not found`);
    }

    this.layer.destroyChildren();

    const background = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.width,
      height: this.height,
      fill: page.background || 'white',
    });
    this.layer.add(background);

    const children = page.children || [];
    const sortedChildren = [...children].sort((a, b) => {
      if (a.alwaysOnTop && !b.alwaysOnTop) return 1;
      if (!a.alwaysOnTop && b.alwaysOnTop) return -1;
      return 0;
    });

    for (const element of sortedChildren) {
      if (element.visible === false) continue;
      await this.renderElement(element);
    }

    this.layer.batchDraw();
  }

  async renderElement(element) {
    const commonProps = {
      id: element.id,
      x: element.x || 0,
      y: element.y || 0,
      width: element.width || 100,
      height: element.height || 100,
      rotation: element.rotation || 0,
      opacity: element.opacity !== undefined ? element.opacity : 1,
      draggable: element.draggable !== undefined ? element.draggable : true,
    };

    let node;

    switch (element.type) {
      case 'text':
        node = await this.createTextElement(element, commonProps);
        break;
      case 'image':
        node = await this.createImageElement(element, commonProps);
        break;
      case 'svg':
        node = await this.createSVGElement(element, commonProps);
        break;
      case 'rect':
        node = this.createRectElement(element, commonProps);
        break;
      default:
        console.warn(`Unknown element type: ${element.type}`);
        return;
    }

    if (node) {
      this.layer.add(node);
    }
  }

  async createTextElement(element, commonProps) {
    const textNode = new Konva.Text({
      ...commonProps,
      text: element.text || '',
      fontSize: element.fontSize || 20,
      fontFamily: element.fontFamily || 'Arial',
      fontStyle: this.getFontStyle(element),
      fill: element.fill || 'black',
      align: element.align || 'left',
      verticalAlign: element.verticalAlign || 'top',
      lineHeight: element.lineHeight || 1.2,
      letterSpacing: element.letterSpacing || 0,
    });

    if (element.backgroundEnabled) {
      const bg = new Konva.Rect({
        x: commonProps.x,
        y: commonProps.y,
        width: commonProps.width,
        height: commonProps.height,
        fill: element.backgroundColor || 'transparent',
        opacity: element.backgroundOpacity || 1,
        cornerRadius: element.backgroundCornerRadius ? commonProps.width * element.backgroundCornerRadius : 0,
      });
      this.layer.add(bg);
    }

    if (element.shadowEnabled) {
      textNode.shadowColor(element.shadowColor || 'black');
      textNode.shadowBlur(element.shadowBlur || 5);
      textNode.shadowOffset({
        x: element.shadowOffsetX || 0,
        y: element.shadowOffsetY || 0
      });
      textNode.shadowOpacity(element.shadowOpacity || 1);
    }

    return textNode;
  }

  getFontStyle(element) {
    const styles = [];
    if (element.fontStyle === 'italic') styles.push('italic');
    if (element.fontWeight === 'bold') styles.push('bold');
    return styles.join(' ');
  }

  async createImageElement(element, commonProps) {
    return new Promise((resolve) => {
      const imageObj = new Image();
      imageObj.crossOrigin = 'anonymous';
      
      imageObj.onload = () => {
        const image = new Konva.Image({
          ...commonProps,
          image: imageObj,
          crop: element.cropX ? {
            x: element.cropX * imageObj.width,
            y: element.cropY * imageObj.height,
            width: element.cropWidth * imageObj.width,
            height: element.cropHeight * imageObj.height,
          } : undefined,
          cornerRadius: element.cornerRadius || 0,
        });

        if (element.filters) {
          this.applyFilters(image, element);
        }

        resolve(image);
      };

      imageObj.onerror = () => {
        console.error(`Failed to load image: ${element.src}`);
        resolve(null);
      };

      imageObj.src = element.src;
    });
  }

  async createSVGElement(element, commonProps) {
    return new Promise((resolve) => {
      const imageObj = new Image();
      imageObj.crossOrigin = 'anonymous';
      
      imageObj.onload = () => {
        const image = new Konva.Image({
          ...commonProps,
          image: imageObj,
        });
        resolve(image);
      };

      imageObj.onerror = () => {
        console.error(`Failed to load SVG: ${element.src}`);
        resolve(null);
      };

      imageObj.src = element.src;
    });
  }

  createRectElement(element, commonProps) {
    return new Konva.Rect({
      ...commonProps,
      fill: element.fill || 'gray',
      stroke: element.stroke || 'black',
      strokeWidth: element.strokeWidth || 0,
      cornerRadius: element.cornerRadius || 0,
    });
  }

  applyFilters(node, element) {
    if (element.blurEnabled) {
      node.filters([Konva.Filters.Blur]);
      node.blurRadius(element.blurRadius || 10);
    }
    if (element.grayscaleEnabled) {
      node.filters([Konva.Filters.Grayscale]);
    }
  }

  addElement(element, pageIndex = this.currentPageIndex) {
    if (!this.polotnoData || !this.polotnoData.pages[pageIndex]) {
      throw new Error('No page loaded');
    }

    const newElement = {
      id: element.id || `element-${Date.now()}`,
      type: element.type || 'text',
      x: element.x || 0,
      y: element.y || 0,
      width: element.width || 100,
      height: element.height || 100,
      ...element,
    };

    this.polotnoData.pages[pageIndex].children.push(newElement);
    this.render(pageIndex);
    return newElement;
  }

  updateElement(elementId, updates, pageIndex = this.currentPageIndex) {
    if (!this.polotnoData || !this.polotnoData.pages[pageIndex]) {
      throw new Error('No page loaded');
    }

    const page = this.polotnoData.pages[pageIndex];
    const elementIndex = page.children.findIndex(el => el.id === elementId);

    if (elementIndex === -1) {
      throw new Error(`Element ${elementId} not found`);
    }

    page.children[elementIndex] = {
      ...page.children[elementIndex],
      ...updates,
    };

    this.render(pageIndex);
    return page.children[elementIndex];
  }

  removeElement(elementId, pageIndex = this.currentPageIndex) {
    if (!this.polotnoData || !this.polotnoData.pages[pageIndex]) {
      throw new Error('No page loaded');
    }

    const page = this.polotnoData.pages[pageIndex];
    page.children = page.children.filter(el => el.id !== elementId);
    this.render(pageIndex);
  }

  getElementById(elementId, pageIndex = this.currentPageIndex) {
    if (!this.polotnoData || !this.polotnoData.pages[pageIndex]) {
      return null;
    }

    const page = this.polotnoData.pages[pageIndex];
    return page.children.find(el => el.id === elementId);
  }

  exportJSON() {
    return JSON.stringify(this.polotnoData, null, 2);
  }

  getJSONObject() {
    return this.polotnoData;
  }

  async exportImage(format = 'png', quality = 1) {
    if (!this.stage) {
      throw new Error('Stage not initialized');
    }

    const dataURL = this.stage.toDataURL({
      mimeType: `image/${format}`,
      quality: quality,
      pixelRatio: 2,
    });

    return dataURL;
  }

  async exportImageBlob(format = 'png', quality = 1) {
    const dataURL = await this.exportImage(format, quality);
    const response = await fetch(dataURL);
    const blob = await response.blob();
    return blob;
  }

  async renderToImage(pageIndex = 0, format = 'png') {
    const response = await fetch(`${this.apiUrl}/render`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        json: this.polotnoData,
        pageIndex: pageIndex,
        format: format,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to render image');
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.apiUrl}/upload-image`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    const data = await response.json();
    return data.url;
  }

  async getFonts() {
    const response = await fetch(`${this.apiUrl}/fonts`);
    if (!response.ok) {
      throw new Error('Failed to fetch fonts');
    }
    const data = await response.json();
    return data.fonts;
  }

  setSize(width, height) {
    this.width = width;
    this.height = height;
    if (this.polotnoData) {
      this.polotnoData.width = width;
      this.polotnoData.height = height;
    }
    if (this.stage) {
      const containerWidth = this.container.offsetWidth || width;
      const scale = containerWidth / width;
      this.stage.width(containerWidth);
      this.stage.height(height * scale);
      this.stage.scaleX(scale);
      this.stage.scaleY(scale);
      this.render(this.currentPageIndex);
    }
  }

  destroy() {
    if (this.stage) {
      this.stage.destroy();
    }
  }
}

export default PolotnoSDK;